import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Agenda } from "@/components/club/Agenda";
import { ClassForm, CompForm, NewsForm } from "@/components/club/ManageForms";
import { Codes, Members } from "@/components/club/ManageAccess";
import { PushToggle } from "@/components/PushToggle";
import { GroupChat } from "@/components/club/GroupChat";
import { ManagePasses, MyPasses } from "@/components/club/Passes";
import { card, dayLabel, ROLE, Tag } from "@/components/club/ui";

const MANAGE = [["clase", "Nueva clase"], ["concurso", "Nuevo concurso"], ["aviso", "Nuevo aviso"], ["bonos", "Bonos"], ["claves", "Claves y grupos"], ["miembros", "Miembros"]];

const Manage = ({ club, comps, onChange, reloadClub }) => {
  const [tab, setTab] = useState("clase");
  const [members, setMembers] = useState([]);
  const loadMembers = () => api.get(`/club/clubs/${club.id}/members`).then((r) => setMembers(r.data));
  useEffect(() => { loadMembers(); }, [club.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const admin = club.role === "admin";
  const tabs = admin ? MANAGE : MANAGE.filter(([k]) => k === "clase" || k === "aviso");
  const instructors = members.filter((m) => m.role === "admin" || m.role === "instructor");
  return (
    <div data-testid="club-manage">
      <div className="flex flex-wrap gap-2 mb-5">{tabs.map(([k, v]) => <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 rounded-full font-bold text-sm ${tab === k ? "bg-[#3D88B8] text-white" : "bg-slate-100 text-slate-700"}`} data-testid={`manage-tab-${k}`}>{v}</button>)}</div>
      {tab === "clase" && <ClassForm clubId={club.id} groups={club.groups} instructors={instructors} onDone={onChange} />}
      {tab === "concurso" && <CompForm clubId={club.id} onDone={onChange} />}
      {tab === "aviso" && <NewsForm clubId={club.id} groups={club.groups} onDone={onChange} />}
      {tab === "claves" && <Codes clubId={club.id} groups={club.groups} comps={comps.filter((c) => c.visibility === "private")} onGroups={reloadClub} />}
      {tab === "bonos" && <ManagePasses clubId={club.id} members={members} />}
      {tab === "miembros" && <Members clubId={club.id} rows={members} onChange={loadMembers} />}
    </div>
  );
};

export default function ClubPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [search] = useSearchParams();
  const chatParam = search.get("chat");
  const [club, setClub] = useState(null);
  const [tab, setTab] = useState(chatParam ? "chat" : "agenda");
  const [comps, setComps] = useState([]);
  const [news, setNews] = useState([]);
  const [key, setKey] = useState(0);
  const loadClub = () => api.get(`/club/clubs/${id}`).then((r) => setClub(r.data)).catch(() => setClub(false));
  const load = () => {
    api.get("/club/competitions", { params: { club_id: id } }).then((r) => setComps(r.data));
    api.get(`/club/clubs/${id}/news`).then((r) => setNews(r.data));
    setKey((k) => k + 1);
  };
  useEffect(() => { loadClub(); load(); }, [id, user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (club === false) return <p className="max-w-4xl mx-auto px-4 py-20 text-center">Club no encontrado. <Link to="/club" className="text-[#3D88B8] font-bold">Volver</Link></p>;
  if (!club) return <p className="max-w-4xl mx-auto px-4 py-20 text-center text-slate-500">Cargando…</p>;
  const staff = club.role === "admin" || club.role === "instructor";
  const tabs = [["agenda", "Agenda"], ["concursos", "Concursos"], ["avisos", "Avisos"], ...(club.role && club.role !== "public" ? [["bonos", "Mis bonos"], ["chat", "Chat"]] : []), ...(staff ? [["gestion", "Gestión"]] : [])];

  return (
    <div data-testid="club-page">
      <div className="bg-[#0B1D3A] text-white">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <Link to="/club" className="text-sm text-blue-200 hover:text-white">← FAUNNY Club</Link>
          <h1 className="font-display font-black uppercase text-4xl sm:text-5xl mt-2" data-testid="club-name">{club.name}</h1>
          <p className="text-blue-100 mt-2">{club.city}{club.description ? ` · ${club.description}` : ""}</p>
          {club.role && <p className="mt-3"><span className="bg-white/15 rounded-full px-3 py-1 text-sm font-bold">Tu rol: {ROLE[club.role]}</span></p>}
          {club.role && <PushToggle />}
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex gap-1 border-b border-slate-200 mb-6 overflow-x-auto">{tabs.map(([k, v]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-5 py-3 font-bold border-b-4 -mb-px whitespace-nowrap transition-colors ${tab === k ? "border-[#B23A38] text-[#0B1D3A]" : "border-transparent text-slate-500"}`} data-testid={`club-tab-${k}`}>{v}</button>))}</div>
        {tab === "agenda" && <Agenda key={key} clubId={id} />}
        {tab === "concursos" && (!comps.length ? <p className="text-slate-500">No hay concursos.</p> : <div className="grid sm:grid-cols-2 gap-4">{comps.map((c) => (
          <Link key={c.id} to={`/club/concurso/${c.id}`} className={`${card} hover:border-[#B23A38]`} data-testid={`club-comp-${c.id}`}>
            <p className="text-sm font-bold text-[#B23A38]">{dayLabel(c.date)}</p><p className="font-bold text-lg">{c.name}</p>
            <div className="mt-2 flex gap-1.5">{c.visibility === "private" && <Tag tone="gray">Cerrado</Tag>}<Tag tone={c.status === "open" ? "green" : "gray"}>{{ open: "Inscripción abierta", closed: "Orden publicado", finished: "Finalizado" }[c.status]}</Tag></div>
          </Link>))}</div>)}
        {tab === "avisos" && (!news.length ? <p className="text-slate-500">No hay avisos.</p> : <ul className="space-y-3">{news.map((n) => (
          <li key={n.id} className={card} data-testid={`news-${n.id}`}><p className="text-xs text-slate-500">{n.created_at.slice(0, 10)} · {n.author}</p><p className="font-bold text-lg">{n.title}</p><p className="whitespace-pre-line text-slate-700 mt-1">{n.body}</p></li>))}</ul>)}
        {tab === "bonos" && <MyPasses clubId={id} />}
        {tab === "chat" && <GroupChat clubId={id} initial={chatParam} />}
        {tab === "gestion" && staff && <Manage club={club} comps={comps} onChange={load} reloadClub={loadClub} />}
      </div>
    </div>
  );
}
